// src/App.tsx
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from "react-router-dom";
import { AppBar, Toolbar, Typography, Button, Container, ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import OnboardingFlow from "./components/onboarding/OnboardingFlow.tsx";
import AuthPage from "./pages/AuthPage.tsx";
import Navigation from "./components/shared/Navigation.tsx";
import Home from "./pages/Home.tsx";
import DashboardContainer from "./components/dashboard/DashboardContainer.tsx";
import MainDashboard from "./components/dashboard/MainDashboard";
import ProgressTracking from "./components/dashboard/ProgressTracking.tsx";
import SubjectSelection from "./components/dashboard/SubjectSelection.tsx";
import LearningModes from "./components/dashboard/LearningModes";
import LearningHub from "./pages/LearningHub.tsx";
import Assessment from "./pages/Assessment";
import Analytics from "./pages/Analytics";
import Settings from "./pages/Settings";
import LearningFlow from "./components/learning/LearningFlow.tsx";
import ApplicationForm from "./components/crowdfunding/ApplicationForm.tsx";
import CriteriaReview from "./components/crowdfunding/CriteriaReview.tsx";
import DocumentUpload from "./components/crowdfunding/DocumentUpload.tsx";
import ParentalConsent from "./components/crowdfunding/ParentalConsent";
import TeacherRecommendations from "./components/crowdfunding/TeacherRecommendation.tsx";
import LoginPage from "./components/auth/Login.tsx";

import RequireAuth from "./components/RequireAuth.tsx";
import SplashScreen from "./components/SplashScreen.tsx";
import { AuthProvider, useAuth } from "./contexts/AuthContext.tsx";
import './assets/styles/mobile-viewport.css';
import './styles/mobile-responsive.css';
import { UserProvider, useUser } from "./contexts/UserContext.tsx";
import { LearningProvider } from "./contexts/LearningContext.tsx";
import { NotificationProvider } from "./contexts/NotificationContext.tsx";
import HeartLessonPage from './pages/lessons/HeartLessonPage.tsx';
import CurriculumPage from './pages/curriculum.tsx';
import ContentCreatorDashboard from './components/content-creator/Dashboard.tsx';
import TeacherDashboard from './components/teacher/dashboard/Dashboard.tsx';
import TeacherRegistration from './components/teacher/steps/TeacherRegistration';
import TeacherJourney from './components/teacher/TeacherJourney';
import SchoolManagement from './components/admin/SchoolManagement';
import AdminDashboard from './components/admin/AdminDashboard';
import StudentsPage from './pages/StudentsPage';
import CompeteRegister from './components/auth/CompeteRegister';
import CompeteDashboard from './components/compete/CompeteDashboard';
import UnifiedRegistration from './components/auth/UnifiedRegistration';
import EnhancedStudentRegister from './components/auth/EnhancedStudentRegister';
import { CompetitionProvider } from './contexts/CompetitionContext';
import MLDemo from './pages/MLDemo';
import { useSession } from './hooks/useSession';
import SessionStatus from './components/SessionStatus';

// Component to redirect users based on their role
const RoleBasedRedirect: React.FC = () => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }
  
  // Redirect based on user role
  if (user.role === 'admin') {
    return <Navigate to="/admin" replace />;
  } else if (user.role === 'content_creator') {
    return <Navigate to="/content-creator/dashboard" replace />;
  } else if (user.role === 'teacher') {
    return <Navigate to="/teacher/dashboard" replace />;
  } else if (user.role === 'competitor') {
    return <Navigate to="/app/compete/dashboard" replace />;
  } else if (user.onboardingCompleted) {
    return <Navigate to="/app/dashboard" replace />;
  } else {
    return <Navigate to="/onboarding" replace />;
  }
};

// Smart root redirect component that shows splash only for new users
const SmartRootRedirect: React.FC = () => {
  const { user, loading } = useAuth();
  
  // If still loading, show splash
  if (loading) {
    return <SplashScreen />;
  }
  
  // If user is authenticated, redirect to appropriate dashboard
  if (user) {
    return <RoleBasedRedirect />;
  }
  
  // If no user, show splash for new users
  return <SplashScreen />;
};

interface Student {
  _id: string;
  firstName: string;
  lastName: string;
  grade: string;
  email: string;
}

interface Class {
  _id: string;
  name: string;
  grade: string;
  students: Student[];
}

// Create MUI theme
const muiTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

// Create styled components
const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: '64px',
}));

// Create Chakra theme
const chakraTheme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  styles: {
    global: {
      body: {
        bg: 'white',
        color: 'gray.800',
      },
    },
  },
});

const TeacherDashboardWrapper = () => {
  const { profile } = useUser();
  
  if (!profile) {
    return null; // or some loading state
  }

  const teacherData = {
    firstName: profile.name?.split(' ')[0] || '',
    lastName: profile.name?.split(' ').slice(1).join(' ') || '',
    email: profile.email,
    schoolName: profile.school || 'Unknown School',
    schoolId: profile.schoolId
  };

  const handleSelectClass = (classItem: Class) => {
    // Handle class selection - for now just log it
    console.log('Selected class:', classItem);
  };

  return <TeacherDashboard teacherData={teacherData} onSelectClass={handleSelectClass} />;
};

// Session Manager Component
const SessionManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useSession(); // This will handle session management
  return (
    <>
      {children}
      <SessionStatus />
    </>
  );
};

const App: React.FC = () => {
  return (
    <ChakraProvider theme={chakraTheme}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <AuthProvider>
          <UserProvider>
            <LearningProvider>
              <NotificationProvider>
                <SessionManager>
                  <Router>
                    <AppBar position="static" color="primary">
                      <Toolbar>
                        <img src="/lavernAi_parent_logo.jpeg" alt="LavernAI Logo" style={{ height: 40, marginRight: 16, borderRadius: 8, background: '#fff' }} />
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                          Lavern Titan
                        </Typography>
                      </Toolbar>
                    </AppBar>

                    <Container>
                      <Routes>
                        {/* Render SmartRootRedirect at the root */}
                        <Route path="/" element={<SmartRootRedirect />} />

                        {/* ==================== */}
                        {/* Authentication Routes */}
                        {/* ==================== */}
                        <Route path="/auth/*" element={<AuthPage />}>
                          <Route index element={<LoginPage />} />
                          <Route path="login" element={<LoginPage />} />
                          <Route path="unified-registration" element={<UnifiedRegistration />} />
                          <Route path="enhanced-student-register" element={<EnhancedStudentRegister />} />
                          <Route path="register" element={<Navigate to="/auth/unified-registration" replace />} />
                          <Route path="compete-register" element={
                            <CompetitionProvider>
                              <CompeteRegister />
                            </CompetitionProvider>
                          } />
                          <Route path="teacher-registration" element={<TeacherRegistration />} />
                          <Route path="teacher-journey" element={<TeacherJourney />} />
                        </Route>

                        {/* ==================== */}
                        {/* Protected Onboarding & Main Application Routes */}
                        {/* ==================== */}
                        <Route
                          path="/onboarding"
                          element={
                            <RequireAuth>
                              <OnboardingFlow />
                            </RequireAuth>
                          }
                        />

                        {/*
                          Learning Session Flow:
                          This new route isolates the learning session flow, which includes term selection,
                          subject selection, strand/sub-strand loading, and lesson viewing.
                        */}
                        <Route
                          path="/learning/*"
                          element={
                            <RequireAuth>
                              <LearningFlow />
                            </RequireAuth>
                          }
                        />

                        <Route
                          path="/app/*"
                          element={
                            <RequireAuth>
                              <Navigation />
                            </RequireAuth>
                          }
                        >
                          <Route index element={<Home />} />
                          <Route path="compete/dashboard" element={
                            <CompetitionProvider>
                              <CompeteDashboard />
                            </CompetitionProvider>
                          } />
                          <Route path="competition" element={
                            <CompetitionProvider>
                              <CompeteDashboard />
                            </CompetitionProvider>
                          } />
                          <Route path="dashboard/*" element={<DashboardContainer />}>
                            <Route index element={<MainDashboard />} />
                            <Route path="progress" element={<ProgressTracking />} />
                            <Route path="subjects" element={<SubjectSelection />} />
                            <Route path="learning-modes" element={<LearningModes />} />
                          </Route>
                          <Route path="learninghub" element={<LearningHub />} />
                          <Route path="assessment" element={<Assessment />} />
                          <Route path="analytics" element={<Analytics />} />
                          <Route path="settings" element={<Settings />} />

                          {/* Crowdfunding Routes */}
                          <Route
                            path="crowdfunding/application-form"
                            element={<ApplicationForm />}
                          />
                          <Route
                            path="crowdfunding/criteria-review"
                            element={<CriteriaReview />}
                          />
                          <Route
                            path="crowdfunding/document-upload"
                            element={<DocumentUpload />}
                          />
                          <Route
                            path="crowdfunding/parental-consent/:applicationId"
                            element={<ParentalConsent applicationId="" />}
                          />
                          <Route
                            path="crowdfunding/teacher-recommendations"
                            element={<TeacherRecommendations />}
                          />
                        </Route>

                        <Route path="/lessons/heart" element={<HeartLessonPage />} />

                        <Route path="/curriculum" element={<CurriculumPage />} />

                        {/* ==================== */}
                        {/* Content Creator Protected Route */}
                        {/* ==================== */}
                        <Route
                          path="/content-creator/dashboard"
                          element={
                            <RequireAuth allowedRoles={["content_creator"]}>
                              <ContentCreatorDashboard />
                            </RequireAuth>
                          }
                        />

                        {/* ==================== */}
                        {/* Teacher Protected Route */}
                        {/* ==================== */}
                        <Route
                          path="/teacher/dashboard"
                          element={
                            <RequireAuth allowedRoles={["teacher"]}>
                              <TeacherDashboardWrapper />
                            </RequireAuth>
                          }
                        />

                        {/* ==================== */}
                        {/* Admin Protected Routes */}
                        {/* ==================== */}
                        <Route
                          path="/admin"
                          element={
                            <RequireAuth allowedRoles={["admin"]}>
                              <AdminDashboard />
                            </RequireAuth>
                          }
                        />
                        <Route
                          path="/admin/schools"
                          element={
                            <RequireAuth allowedRoles={["admin"]}>
                              <SchoolManagement />
                            </RequireAuth>
                          }
                        />

                        {/* ==================== */}
                        {/* Student Protected Route */}
                        {/* ==================== */}
                        <Route
                          path="/dashboard"
                          element={
                            <RequireAuth allowedRoles={["student"]}>
                              <MainDashboard />
                            </RequireAuth>
                          }
                        />

                        {/* Add Students page route */}
                        <Route
                          path="/students"
                          element={
                            <RequireAuth>
                              <StudentsPage />
                            </RequireAuth>
                          }
                        />

                        {/* COMPETE Dashboard placeholder - removed duplicate route */}

                        {/* Direct competition route for competitors */}
                        <Route path="/competition" element={
                          <RequireAuth allowedRoles={["competitor"]}>
                            <CompetitionProvider>
                              <CompeteDashboard />
                            </CompetitionProvider>
                          </RequireAuth>
                        } />

                        <Route path="/ml-demo" element={<MLDemo />} />

                        {/* Catch-all route: Redirect based on auth status and user role */}
                        <Route path="*" element={
                          <RequireAuth>
                            <RoleBasedRedirect />
                          </RequireAuth>
                        } />
                      </Routes>
                    </Container>
                  </Router>
                  
                  {/* Toast Container for notifications */}
                  <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="light"
                  />
                </SessionManager>
              </NotificationProvider>
            </LearningProvider>
          </UserProvider>
        </AuthProvider>
      </ThemeProvider>
    </ChakraProvider>
  );
};

export default App;
